import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import 'xterm/css/xterm.css';
import { getAuthToken } from './authToken';

const WS_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/^http/, 'ws')
  : `${window.location.protocol.replace('http', 'ws')}//${window.location.host}`;

/**
 * Imperative xterm.js terminal.
 *
 * Exposed via ref:
 *   clear()                          — reset the screen between exercises
 *   startRun(code, language, onExit) — connect WS, stream output, call onExit(exitCode) when done
 */
const XTerminal = forwardRef(function XTerminal(
  { onRunStateChange, onWsStatusChange, termBg, termText },
  ref
) {
  const containerRef = useRef(null);
  const termRef      = useRef(null);
  const fitAddonRef  = useRef(null);
  const wsRef        = useRef(null);
  const lineRef      = useRef('');   // current input line buffer
  const runningRef   = useRef(false);
  const exitedRef    = useRef(false); // true once exit/error msg received
  const closingRef   = useRef(false); // true when we intentionally close WS
  const disposedRef  = useRef(false); // true once the xterm instance is torn down
  const fitTimerRef  = useRef(null);  // pending initial-fit setTimeout id

  useImperativeHandle(ref, () => ({
    clear() {
      lineRef.current = '';
      termRef.current?.reset();
    },

    startRun(code, language, onExit) {
      // Kill any existing session first
      if (wsRef.current) {
        closingRef.current = true;
        wsRef.current.close();
        wsRef.current = null;
      }

      exitedRef.current  = false;
      closingRef.current = false;
      lineRef.current    = '';
      runningRef.current = true;
      onRunStateChange?.(true);
      onWsStatusChange?.('connecting');

      termRef.current?.reset();

      const ws = new WebSocket(`${WS_BASE}/ws/run`);
      wsRef.current = ws;
      const term = termRef.current;

      ws.onopen = () => {
        onWsStatusChange?.('connected');
        ws.send(JSON.stringify({ type: 'init', language, code, token: getAuthToken() }));
        term?.focus();
      };

      ws.onmessage = ({ data }) => {
        const msg = JSON.parse(data);
        if (msg.type === 'output') {
          term?.write(msg.data);
        } else if (msg.type === 'exit') {
          exitedRef.current  = true;
          runningRef.current = false;
          onRunStateChange?.(false);
          term?.write(`\r\n\x1b[90m[Process exited with code ${msg.code}]\x1b[0m\r\n`);
          onExit?.(msg.code);
        } else if (msg.type === 'error') {
          exitedRef.current  = true;
          runningRef.current = false;
          onRunStateChange?.(false);
          term?.write(`\r\n\x1b[31m[Error: ${msg.data}]\x1b[0m\r\n`);
          onExit?.(-1);
        }
      };

      ws.onerror = () => {
        if (!exitedRef.current && !closingRef.current) {
          runningRef.current = false;
          onRunStateChange?.(false);
          term?.write('\r\n\x1b[31m[Connection error — is the Flask server running?]\x1b[0m\r\n');
          onExit?.(-1);
        }
      };

      ws.onclose = () => {
        // Guard: if startRun() was called again before this ws finished closing,
        // wsRef.current already points to the new ws. Don't clobber runningRef.
        if (wsRef.current !== ws) return;
        onWsStatusChange?.('disconnected');
        if (runningRef.current) {
          runningRef.current = false;
          onRunStateChange?.(false);
        }
      };
    },
  }));

  // Update xterm theme when bg/text tokens change (light ↔ dark toggle)
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;
    term.options.theme = {
      ...term.options.theme,
      background: termBg  || '#211c16',
      foreground: termText || '#f3eadb',
    };
  }, [termBg, termText]);

  // Mount xterm once
  useEffect(() => {
    const bg  = termBg  || '#211c16';
    const fg  = termText || '#f3eadb';
    const term = new Terminal({
      theme: {
        background:          bg,
        foreground:          fg,
        cursor:              '#a880e8',
        cursorAccent:        bg,
        selectionBackground: 'rgba(112,80,184,0.35)',
        black:   '#2a2018', brightBlack:   '#5a5045',
        red:     '#d05050', brightRed:     '#e07070',
        green:   '#4aaa6a', brightGreen:   '#70cc90',
        yellow:  '#c09030', brightYellow:  '#e0b850',
        blue:    '#5090d0', brightBlue:    '#80b8f0',
        magenta: '#9060c8', brightMagenta: '#b090e0',
        cyan:    '#40a890', brightCyan:    '#60c8b0',
        white:   '#d8cfc5', brightWhite:   '#f0e8db',
      },
      fontFamily:  '"JetBrains Mono", "Cascadia Code", "Fira Code", Consolas, "Courier New", monospace',
      fontSize:    13,
      lineHeight:  1.4,
      cursorBlink: true,
      convertEol:  true,
      scrollback:  2000,
    });

    const fitAddon      = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);
    term.open(containerRef.current);

    disposedRef.current = false;

    // Safe fit: no-op once the terminal has been disposed. Guards against a
    // pending fit callback (fonts.ready promise, timeout, or ResizeObserver)
    // firing after unmount, which would trigger a viewport refresh against a
    // torn-down render service (xterm.js Viewport._innerRefresh race).
    const safeFit = () => {
      if (disposedRef.current) return;
      try { fitAddon.fit(); } catch (_) {}
    };

    // Wait for custom fonts to load so character cell measurements are accurate
    document.fonts.ready.then(safeFit);
    // Fallback for immediate layout
    fitTimerRef.current = setTimeout(safeFit, 50);

    termRef.current     = term;
    fitAddonRef.current = fitAddon;

    // Local line-discipline: echo keystrokes, buffer until Enter
    term.onData((data) => {
      if (!runningRef.current) return;
      const ws = wsRef.current;

      if (data === '\r') {
        // Enter — send buffered line to process
        term.write('\r\n');
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'input', data: lineRef.current + '\n' }));
        }
        lineRef.current = '';
      } else if (data === '\x7f') {
        // Backspace
        if (lineRef.current.length > 0) {
          lineRef.current = lineRef.current.slice(0, -1);
          term.write('\b \b');
        }
      } else if (data === '\x03') {
        // Ctrl+C — echo and clear buffer (process will receive the signal via broken pipe)
        term.write('^C\r\n');
        lineRef.current = '';
      } else if (data === '\x04') {
        // Ctrl+D — send EOF
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'input', data: lineRef.current + '\n' }));
        }
        lineRef.current = '';
      } else if (data.length === 1 && data.charCodeAt(0) >= 32) {
        // Printable character — echo locally and add to buffer
        lineRef.current += data;
        term.write(data);
      }
    });

    const ro = new ResizeObserver(safeFit);
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      // Stop any further fit/refresh work before tearing down xterm. Order
      // matters: silence the callbacks first, then dispose the addon and close
      // the WS, and only then dispose the terminal itself.
      disposedRef.current = true;
      ro.disconnect();
      if (fitTimerRef.current) {
        clearTimeout(fitTimerRef.current);
        fitTimerRef.current = null;
      }
      closingRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
      try { fitAddon.dispose(); } catch (_) {}
      term.dispose();
      termRef.current     = null;
      fitAddonRef.current = null;
    };
    // Mount-once setup: termBg/termText are read for the initial xterm theme,
    // but re-running would recreate the terminal on every theme toggle. The
    // live theme update is handled separately, so this effect stays mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} className="xterm-host" />;
});

export default XTerminal;
